import { Check, Plus, X } from 'lucide-react'
import { useCallback, useEffect, useMemo, useState, type ReactNode } from 'react'
import { Button } from '../components/Button'
import { SearchInput, SegmentedControl } from '../components/FilterBar'
import { Modal } from '../components/Modal'
import { PageHeader } from '../components/PageHeader'
import { Select } from '../components/Select'
import { useAppData } from '../context/AppDataContext'
import { useAuth } from '../context/AuthContext'
import { useToast } from '../context/ToastContext'
import { api, apiErrorMessage } from '../lib/api'
import { cn } from '../lib/cn'
import { formatDateLong } from '../lib/format'

/*
 * Who the studio is pitching.
 *
 * Prospects had a tab on the clients page that only a manager could add to.
 * Now anybody adds the ones they are chasing and owns them, everybody sees the
 * whole pipeline, and each one carries the owner's read of how it is going.
 * Signing one, or recording a no, is still a manager's call: it moves the
 * prospect onto the clients page, or into its previous clients.
 */

type Chance = 'possible' | 'maybe' | 'unlikely'
type WouldBe = 'recurring' | 'one_off'

interface Prospect {
  id: string
  name: string
  chance: Chance | null
  owner_id: string | null
  owner_name: string | null
  note: string | null
  would_be: WouldBe
  added_at: string
  mine: boolean
}

const CHANCES: { value: Chance; label: string; on: string }[] = [
  { value: 'possible', label: 'Possible', on: 'border-emerald-300 bg-emerald-50 text-emerald-700' },
  { value: 'maybe', label: 'Maybe', on: 'border-amber-300 bg-amber-50 text-amber-700' },
  { value: 'unlikely', label: 'Not possible', on: 'border-rose-300 bg-rose-50 text-rose-700' },
]

const WOULD_BE: Record<WouldBe, string> = { recurring: 'Monthly', one_off: 'One-off job' }

type View = 'mine' | 'all'

export default function Prospects() {
  const { hasRole } = useAuth()
  const { directory, reload } = useAppData()
  const { showToast } = useToast()
  const manages = hasRole('PARTNER', 'MANAGER')
  const reads = hasRole('VIEWER')

  const [rows, setRows] = useState<Prospect[] | null>(null)
  const [failed, setFailed] = useState<string | null>(null)
  const [view, setView] = useState<View | null>(null)
  const [search, setSearch] = useState('')
  const [chance, setChance] = useState<Chance | 'all'>('all')
  const [adding, setAdding] = useState(false)
  const [busy, setBusy] = useState<string | null>(null)

  const load = useCallback(async () => {
    try {
      setRows(await api.get<Prospect[]>('/prospects'))
      setFailed(null)
    } catch (err) {
      setFailed(apiErrorMessage(err))
    }
  }, [])

  useEffect(() => {
    void load()
  }, [load])

  const mine = useMemo(() => (rows ?? []).filter((p) => p.mine), [rows])
  // Opens on your own list when you have one; otherwise on everybody's.
  const shown: View = view ?? (mine.length > 0 ? 'mine' : 'all')
  const inView = shown === 'mine' ? mine : (rows ?? [])

  const visible = useMemo(
    () =>
      inView.filter(
        (p) =>
          (chance === 'all' || p.chance === chance) &&
          (search === '' ||
            p.name.toLowerCase().includes(search.toLowerCase()) ||
            (p.owner_name ?? '').toLowerCase().includes(search.toLowerCase())),
      ),
    [inView, chance, search],
  )

  const tally = useMemo(() => {
    const n: Record<Chance, number> = { possible: 0, maybe: 0, unlikely: 0 }
    for (const p of inView) if (p.chance !== null) n[p.chance] += 1
    return n
  }, [inView])

  async function change(p: Prospect, body: Record<string, unknown>, said?: string) {
    if (busy !== null) return
    setBusy(p.id)
    try {
      const next = await api.patch<Prospect>(`/prospects/${encodeURIComponent(p.id)}`, body)
      setRows((all) => (all ?? []).map((r) => (r.id === p.id ? next : r)))
      if (said !== undefined) showToast(said)
    } catch (err) {
      showToast(apiErrorMessage(err))
    } finally {
      setBusy(null)
    }
  }

  async function settle(p: Prospect, outcome: 'active' | 'lost') {
    if (busy !== null) return
    setBusy(p.id)
    try {
      await api.patch(`/clients/${encodeURIComponent(p.id)}`, { status: outcome })
      setRows((all) => (all ?? []).filter((r) => r.id !== p.id))
      reload()
      showToast(
        outcome === 'active'
          ? `${p.name} signed. They are on the clients page now.`
          : `${p.name} said no. They are under Previous on the clients page.`,
      )
    } catch (err) {
      showToast(apiErrorMessage(err))
    } finally {
      setBusy(null)
    }
  }

  return (
    <div className="flex flex-col gap-5">
      <PageHeader
        variant="dark"
        title="Prospects"
        description="Who the studio is pitching, who is chasing each one, and how it looks."
        actions={
          reads ? undefined : (
            <Button onClick={() => setAdding(true)}>
              <Plus size={16} /> Add a prospect
            </Button>
          )
        }
      />

      <div className="flex flex-wrap items-center gap-3">
        <SegmentedControl
          value={shown}
          onChange={(v) => setView(v as View)}
          options={[
            { label: `My prospects · ${mine.length}`, value: 'mine' },
            { label: `All prospects · ${rows?.length ?? 0}`, value: 'all' },
          ]}
        />
        <SearchInput value={search} onChange={setSearch} placeholder="Search name or owner…" className="w-full sm:w-64" />
      </div>

      <div className="flex flex-wrap gap-2" role="group" aria-label="Show only">
        {CHANCES.map((c) => (
          <button
            key={c.value}
            type="button"
            onClick={() => setChance(chance === c.value ? 'all' : c.value)}
            aria-pressed={chance === c.value}
            className={cn(
              'rounded-full border px-3 py-1 text-xs font-medium transition-colors',
              chance === c.value ? c.on : 'border-shell-border bg-shell text-shell-text hover:text-shell-text-active',
            )}
          >
            {c.label} · {tally[c.value]}
          </button>
        ))}
      </div>

      {failed !== null ? (
        <Empty title="The prospects did not load" body={failed} />
      ) : rows === null ? (
        <p className="text-sm text-shell-text">Loading…</p>
      ) : visible.length === 0 ? (
        <Empty
          title={shown === 'mine' && mine.length === 0 ? 'You have no prospects yet' : 'Nothing matches'}
          body={
            shown === 'mine' && mine.length === 0
              ? 'Add somebody you are pitching. They stay yours, and the rest of the team sees them under All prospects.'
              : 'Try another name, or clear the filter.'
          }
        />
      ) : (
        <ul className="flex flex-col gap-3">
          {visible.map((p) => (
            <ProspectCard
              key={p.id}
              p={p}
              canEdit={!reads && (p.mine || manages)}
              manages={manages}
              busy={busy === p.id}
              people={directory}
              onChance={(c) => change(p, { chance: c })}
              onNote={(note) => change(p, { note }, 'Note saved.')}
              onOwner={(ownerId) => change(p, { ownerId }, 'Handed over.')}
              onSettle={(outcome) => settle(p, outcome)}
            />
          ))}
        </ul>
      )}

      <AddProspect
        open={adding}
        manages={manages}
        people={directory}
        onClose={() => setAdding(false)}
        onAdded={(p) => {
          // Fetched again rather than appended, so it lands in likeliest-first order.
          void load()
          setView('mine')
          reload()
          showToast(`${p.name} added to your prospects.`)
        }}
      />
    </div>
  )
}

function ProspectCard({
  p,
  canEdit,
  manages,
  busy,
  people,
  onChance,
  onNote,
  onOwner,
  onSettle,
}: {
  p: Prospect
  canEdit: boolean
  manages: boolean
  busy: boolean
  people: { id: string; name: string }[]
  onChance: (c: Chance | null) => void
  onNote: (note: string) => void
  onOwner: (ownerId: string) => void
  onSettle: (outcome: 'active' | 'lost') => void
}) {
  const [note, setNote] = useState(p.note ?? '')
  const [editing, setEditing] = useState(false)

  useEffect(() => {
    if (!editing) setNote(p.note ?? '')
  }, [p.note, editing])

  return (
    <li className={cn('rounded-2xl border border-shell-border bg-shell p-4', busy && 'opacity-60')}>
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0">
          <h3 className="truncate text-base font-semibold text-shell-text-active">{p.name}</h3>
          <p className="mt-0.5 text-xs text-shell-text">
            {p.owner_name ?? 'Nobody owns this yet'} · {WOULD_BE[p.would_be]} · added {formatDateLong(p.added_at.slice(0, 10))}
          </p>
        </div>
        <div className="flex flex-wrap gap-1.5" role="group" aria-label={`How ${p.name} looks`}>
          {CHANCES.map((c) =>
            canEdit ? (
              <button
                key={c.value}
                type="button"
                disabled={busy}
                aria-pressed={p.chance === c.value}
                onClick={() => onChance(p.chance === c.value ? null : c.value)}
                className={cn(
                  'rounded-full border px-3 py-1 text-xs font-medium transition-colors',
                  p.chance === c.value
                    ? c.on
                    : 'border-shell-border bg-shell text-shell-text hover:text-shell-text-active',
                )}
              >
                {c.label}
              </button>
            ) : p.chance === c.value ? (
              <span key={c.value} className={cn('rounded-full border px-3 py-1 text-xs font-medium', c.on)}>
                {c.label}
              </span>
            ) : null,
          )}
          {!canEdit && p.chance === null && <span className="text-xs text-shell-text">Not judged yet</span>}
        </div>
      </div>

      {editing ? (
        <div className="mt-3 flex flex-col gap-2">
          <textarea
            value={note}
            onChange={(e) => setNote(e.target.value)}
            rows={3}
            autoFocus
            placeholder="Who the contact is, what was pitched, what happens next"
            className="field-input"
          />
          <div className="flex gap-2">
            <Button
              size="sm"
              onClick={() => {
                onNote(note.trim())
                setEditing(false)
              }}
            >
              Save note
            </Button>
            <Button size="sm" variant="secondary" onClick={() => setEditing(false)}>
              Cancel
            </Button>
          </div>
        </div>
      ) : p.note !== null ? (
        <p className="mt-3 whitespace-pre-wrap text-sm text-shell-text-active">{p.note}</p>
      ) : null}

      {canEdit && !editing && (
        <div className="mt-3 flex flex-wrap items-center gap-2 border-t border-shell-border pt-3">
          <Button size="sm" variant="ghost" onClick={() => setEditing(true)}>
            {p.note === null ? 'Add a note' : 'Edit note'}
          </Button>
          <label className="flex items-center gap-2 text-xs text-shell-text">
            Owner
            <span className="w-32">
              <Select
                value={p.owner_id ?? ''}
                onChange={(e) => onOwner(e.target.value)}
                disabled={busy}
                aria-label={`Who owns ${p.name}`}
                className="field-input h-8 py-0 text-xs"
              >
                {p.owner_id === null && <option value="">Nobody</option>}
                {people.map((m) => (
                  <option key={m.id} value={m.id}>
                    {m.name}
                  </option>
                ))}
              </Select>
            </span>
          </label>
          {manages && (
            <div className="ml-auto flex gap-2">
              <Button size="sm" variant="secondary" disabled={busy} onClick={() => onSettle('lost')}>
                <X size={14} /> Said no
              </Button>
              <Button size="sm" disabled={busy} onClick={() => onSettle('active')}>
                <Check size={14} /> Signed
              </Button>
            </div>
          )}
        </div>
      )}
    </li>
  )
}

function AddProspect({
  open,
  manages,
  people,
  onClose,
  onAdded,
}: {
  open: boolean
  manages: boolean
  people: { id: string; name: string }[]
  onClose: () => void
  onAdded: (p: Prospect) => void
}) {
  const { showToast } = useToast()
  const [name, setName] = useState('')
  const [chance, setChance] = useState<Chance | null>(null)
  const [wouldBe, setWouldBe] = useState<WouldBe>('recurring')
  const [note, setNote] = useState('')
  const [owner, setOwner] = useState('')
  const [saving, setSaving] = useState(false)

  useEffect(() => {
    if (!open) return
    setName('')
    setChance(null)
    setWouldBe('recurring')
    setNote('')
    setOwner('')
  }, [open])

  async function save() {
    if (name.trim() === '' || saving) return
    setSaving(true)
    try {
      const added = await api.post<Prospect>('/prospects', {
        name: name.trim(),
        wouldBe,
        ...(chance !== null ? { chance } : {}),
        ...(note.trim() !== '' ? { note: note.trim() } : {}),
        ...(manages && owner !== '' ? { ownerId: owner } : {}),
      })
      onAdded(added)
      onClose()
    } catch (err) {
      showToast(apiErrorMessage(err))
    } finally {
      setSaving(false)
    }
  }

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Add a prospect"
      description="Somebody the studio is pitching. It is yours, and the whole team can see it."
      footer={
        <>
          <Button variant="secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button onClick={save} disabled={name.trim() === '' || saving}>
            Add prospect
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-4">
        <Field label="Name">
          <input
            value={name}
            onChange={(e) => setName(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') void save()
            }}
            placeholder="e.g. Lakeview Dental"
            className="field-input"
            autoFocus
          />
        </Field>
        <Field label="How it looks">
          <div className="flex flex-wrap gap-1.5">
            {CHANCES.map((c) => (
              <button
                key={c.value}
                type="button"
                aria-pressed={chance === c.value}
                onClick={() => setChance(chance === c.value ? null : c.value)}
                className={cn(
                  'rounded-full border px-3 py-1.5 text-xs font-medium transition-colors',
                  chance === c.value ? c.on : 'border-shell-border bg-shell text-shell-text hover:text-shell-text-active',
                )}
              >
                {c.label}
              </button>
            ))}
          </div>
        </Field>
        <div className={cn('grid gap-3', manages && 'sm:grid-cols-2')}>
          <Field label="If they sign">
            <Select value={wouldBe} onChange={(e) => setWouldBe(e.target.value as WouldBe)} className="field-input">
              <option value="recurring">Monthly, recurring</option>
              <option value="one_off">One-off job</option>
            </Select>
          </Field>
          {manages && (
            <Field label="Owner">
              <Select value={owner} onChange={(e) => setOwner(e.target.value)} className="field-input">
                <option value="">Me</option>
                {people.map((m) => (
                  <option key={m.id} value={m.id}>
                    {m.name}
                  </option>
                ))}
              </Select>
            </Field>
          )}
        </div>
        <Field label="Note">
          <textarea
            value={note}
            onChange={(e) => setNote(e.target.value)}
            rows={3}
            placeholder="Who the contact is, what was pitched, what happens next"
            className="field-input"
          />
        </Field>
      </div>
    </Modal>
  )
}

function Field({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div>
      <label className="field-label">{label}</label>
      {children}
    </div>
  )
}

function Empty({ title, body }: { title: string; body: string }) {
  return (
    <div className="rounded-2xl border border-dashed border-shell-border bg-shell px-6 py-10 text-center">
      <p className="text-sm font-semibold text-shell-text-active">{title}</p>
      <p className="mx-auto mt-1 max-w-md text-sm text-shell-text">{body}</p>
    </div>
  )
}
